import { cloneDeep, toInteger } from 'lodash';
import React from 'react';
import PptxGenJS from '@meitu/js-to-pptx';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import {
    createCanvas,
    createForeignObjectImageUrl,
    fetchVectorText,
    imageConverter,
    isHttpUri,
    loadSvgAsync,
    serializerDomToString,
} from '@/utils/file-kit';
import { deleteExtraInfo, getLayerFreshDOM, getLayerRuntimeValue, setLayerRuntimeValue } from '@/utils/layer-kit';
import {
    transformLetterSpacingToApp,
    transformLetterSpacingToStage,
    transformLineSpacingToApp,
    transformLineSpacingToStage,
} from '@/utils/transform-kit';
import { pxToInch } from '@/utils/unit-kit';
import {
    IActiveBaseInfo,
    IExportBehavior,
    ILayerBehavior,
    ILayerOriginInfo,
    ILayerProps,
    IMarkupLayerProps,
    IRenderLayerExtraProps,
    ISize,
    LayerType,
    MarkupLabelType,
    MarkupTextPositionType,
    MarkupType,
} from '@/version-types';
import LabelKit from './utils/label-kit';
import { clampFillWidth, clampFontSize, getDefaultMarkupPoint, getScreenshotUrlCode } from './utils/layer';
import ActiveOperate from './views/active-operate';
import MarkupLayer from './views/markup-layer';
import { renderDom } from './views/render-dom';

function scaleMarkupLayer(layer: ILayerProps, ratio: number, originInfo: any) {
    const { anchor: oldAnchor, label, fill, centerOffset } = originInfo?.data as IMarkupLayerProps;
    const data = layer.data as IMarkupLayerProps;
    data.fill.width = clampFillWidth(fill.width * ratio);

    if (data.markupType === MarkupType.Segment) {
        data.centerOffset.x = centerOffset.x * ratio;
        data.centerOffset.y = centerOffset.y * ratio;
        data.anchor.points![0].x = oldAnchor.points![0].x * ratio;
        data.anchor.points![0].y = oldAnchor.points![0].y * ratio;
        data.anchor.points![1].x = oldAnchor.points![1].x * ratio;
        data.anchor.points![1].y = oldAnchor.points![1].y * ratio;
    }

    if (ratio < 1) {
        data.label.fontSize = clampFontSize(label.fontSize * ratio);
        const labelKit = new LabelKit();
        const res = labelKit.getSize(layer);
        data.label.width = res.width;
        data.label.height = res.height;
        labelKit.destroy();
    }

    if (data.markupType === MarkupType.Segment) {
        data.label.roundWeight = LabelKit.getRoundWeight(layer);
    }

    if (data.markupType === MarkupType.Arrow) {
        const {
            renderArea: { up, down },
        } = LabelKit.getLabelRenderInfo(label.width, data.fill.width, layer);

        data.anchor.arrowLeft[0] = Math.max(0, Math.ceil(oldAnchor.arrowLeft[0] * ratio));
        data.anchor.arrowRight[0] = Math.max(0, Math.ceil(oldAnchor.arrowRight[0] * ratio));
        const upResult = Math.max(data.anchor.arrowLeft[0], data.anchor.arrowRight[0], up);

        data.anchor.arrowLeft[1] = Math.min(0, Math.ceil(oldAnchor.arrowLeft[1] * ratio));
        data.anchor.arrowRight[1] = Math.min(0, Math.ceil(oldAnchor.arrowRight[1] * ratio));
        const downResult = Math.min(data.anchor.arrowLeft[1], data.anchor.arrowRight[1], down);

        if (data.label.position === MarkupTextPositionType.Up) {
            data.centerOffset.y = layer.height + downResult;
        } else {
            data.centerOffset.y = upResult;
        }
    }
}

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <MarkupLayer layer={layer} {...extraProps} />;
        },

        async onBeforeAddOrUpdate(layer) {
            const data = layer.data as IMarkupLayerProps;
            if (!data.startPoint) {
                data.startPoint = getDefaultMarkupPoint();
            }
            if (!data.endPoint) {
                data.endPoint = getDefaultMarkupPoint();
            }
            const __runtime = layer.__runtime;
            if (!__runtime?.isInit) {
                const { label } = data;
                data.label = {
                    ...label,
                };
                data.label.letterSpacing = transformLetterSpacingToStage(label.letterSpacing, label.fontSize);
                data.label.lineSpacing = transformLineSpacingToStage(label.lineSpacing);

                setLayerRuntimeValue(layer, {
                    markupType: data.markupType,
                });
            }
            setLayerRuntimeValue(layer, {
                isInit: true,
            });

            if (!getLayerRuntimeValue(layer, 'markupType')) {
                setLayerRuntimeValue(layer, {
                    markupType: data.markupType,
                });
            }

            if (!data.startPoint.offset) {
                data.startPoint.offset = getDefaultMarkupPoint().offset;
            }

            if (!data.endPoint.offset) {
                data.endPoint.offset = getDefaultMarkupPoint().offset;
            }

            if (data.screenshotUrl && isHttpUri(data.screenshotUrl)) {
                const code = await getScreenshotUrlCode(identity, layer, true);
                data.screenshotUrl = code;
            }
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IMarkupLayerProps;
            deleteExtraInfo(layer);
            // 上传缩略图
            const code = await getScreenshotUrlCode(identity, layer);
            // 获取远程缩略图地址
            data.screenshotUrl = await base64Kit.getRemoteUrlAsync(code);

            const { label } = data;
            data.label = {
                ...label,
            };
            if (getLayerRuntimeValue(layer, 'isInit')) {
                data.label.letterSpacing = transformLetterSpacingToApp(label.letterSpacing, label.fontSize);
                data.label.lineSpacing = transformLineSpacingToApp(label.lineSpacing);
            }

            if (!data.anchor.arrowLeft) {
                data.anchor.arrowLeft = [];
            }
            if (!data.anchor.arrowRight) {
                data.anchor.arrowRight = [];
            }
        },

        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            // console.time('===========生成时间');
            const { width, height, left, top, opacity, rotate } = layer;
            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const centerPoint = {
                x: resultWidth / 2 + left,
                y: resultHeight / 2 + top,
            };
            const drawOffsetPoint = {
                x: -resultWidth / 2,
                y: -resultHeight / 2,
            };
            const layerImage = await layerBehavior.getLayerImageAsync?.(layer, false);
            if (!layerImage) return;
            const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
            destCtx.translate(centerPoint.x, centerPoint.y);
            destCtx.rotate((Math.PI / 180) * rotate);
            // 透明度
            destCtx.globalAlpha = opacity;
            destCtx.drawImage(
                layerImage,
                0,
                0,
                resultWidth,
                resultHeight,
                drawOffsetPoint.x,
                drawOffsetPoint.y,
                resultWidth,
                resultHeight,
            );
            // console.timeEnd('===========生成时间');
            return destCanvas;
        },

        async getLayerImageAsync(layer: ILayerProps, globalAlpha = true) {
            const { width, height, opacity } = layer;
            const {
                label,
                imageTransform: { flipH, flipV },
                startPoint,
                endPoint,
            } = layer.data as IMarkupLayerProps;

            // 重新换算 letterSpacing 和 lineSpacing
            label.letterSpacing = transformLetterSpacingToStage(label.letterSpacing, label.fontSize);
            label.lineSpacing = transformLineSpacingToStage(label.lineSpacing);

            // 加载 svg 内容，为合成做准备
            let loadSvgError = false;
            try {
                if (startPoint.url) {
                    await fetchVectorText(startPoint.url);
                }
                if (endPoint.url) {
                    await fetchVectorText(endPoint.url);
                }
            } catch (error) {
                console.log('load svg error', error);
                loadSvgError = true;
            }

            const selector = `[data-draw-id="${layer.id}"]`;
            const removeDomHandle = await renderDom(layer);
            let baseImage = getLayerFreshDOM(selector) as HTMLImageElement;
            if (!baseImage || loadSvgError) {
                // 获取 html 异常，返回一个空画布
                removeDomHandle?.();
                return createCanvas(1, 1).canvas;
            }
            const framework = getFramework(identity);
            const fontBase64 = framework.getFontBase64(layer);
            const fontFamilyMap = getLayerRuntimeValue<Record<string, string>>(layer, 'fontFamilyMap') || {};
            const fontFamilyStyle = framework.createFontStyle(
                fontFamilyMap[label.fontFamily],
                fontBase64[label.fontFamily],
            );
            const svgDomUrl = createForeignObjectImageUrl(
                width,
                height,
                serializerDomToString(baseImage),
                false,
                fontFamilyStyle,
            );
            baseImage = (await loadSvgAsync(svgDomUrl)) as HTMLImageElement;
            baseImage = (await loadSvgAsync(baseImage.src)) as HTMLImageElement;

            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const offsetX = 0;
            const offsetY = 0;
            const originImgWidth = resultWidth;
            const originImgHeight = resultHeight;
            let imgOffsetX = resultWidth / 2;
            let imgOffsetY = resultHeight / 2;
            if (flipH) {
                imgOffsetX = imgOffsetX - offsetX;
            } else {
                imgOffsetX = imgOffsetX + offsetX;
            }
            if (flipV) {
                imgOffsetY = imgOffsetY - offsetY;
            } else {
                imgOffsetY = imgOffsetY + offsetY;
            }
            // 将图层绘制到画布上
            const getLayerResult = async (layerCanvas: HTMLCanvasElement) => {
                const finalCanvas = layerCanvas;
                if (!globalAlpha) return finalCanvas;
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(finalCanvas.width, finalCanvas.height);
                destCtx.globalAlpha = opacity;
                destCtx.drawImage(finalCanvas, 0, 0, finalCanvas.width, finalCanvas.height);
                return destCanvas;
            };

            //  绘制图层
            const getLayerCanvas = async () => {
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
                destCtx.translate(imgOffsetX, imgOffsetY);
                let rotate = 0;
                if (flipH) {
                    rotate = -rotate;
                }
                if (flipV) {
                    rotate = -rotate;
                }
                destCtx.rotate((rotate * Math.PI) / 180);
                destCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
                destCtx.drawImage(
                    baseImage,
                    -originImgWidth / 2,
                    -originImgHeight / 2,
                    originImgWidth,
                    originImgHeight,
                );
                return destCanvas;
            };

            const layerCanvas = await getLayerCanvas();
            const result = await getLayerResult(layerCanvas);
            removeDomHandle?.();
            return result;
        },

        async savePPTLayerAsync(slide, layer) {
            const { left, top, width, height, rotate } = layer;
            const options: PptxGenJS.ImageProps = {
                // path: url,
                x: pxToInch(left),
                y: pxToInch(top),
                w: pxToInch(width),
                h: pxToInch(height),
                rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
            };

            const canvas = await layerBehavior.getLayerImageAsync?.(layer);

            options.data = canvas?.toDataURL() || '';

            slide.addImage(options);
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            return {
                cutWidth: 10,
                cutHeight: 10,
                data: cloneDeep(layer.data),
            };
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, height, cutHeight, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        onBeforeScaleUpdate(layer: ILayerProps, { originInfo }) {
            const originWidth = originInfo?.width || 1;
            const ratio = layer.width / originWidth;
            scaleMarkupLayer(layer, ratio, originInfo);
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.markup);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            let { screenshotUrl } = layer.data as IMarkupLayerProps;
            if (screenshotUrl) {
                screenshotUrl = base64Kit.getRemoteUrl(screenshotUrl);
            }
            return (
                <div className="l-layer__icon border">
                    {screenshotUrl ? (
                        <img src={imageConverter(screenshotUrl, 32, 100)} alt="" />
                    ) : (
                        <IconFont name="BrushAndBoard" fontSize={16} />
                    )}
                </div>
            );
        },

        customActiveOperate(layer: ILayerProps) {
            return <ActiveOperate layer={layer} />;
        },

        onBeforeCrop(layer, scale) {
            scaleMarkupLayer(layer, scale, cloneDeep(layer));
        },

        getTextInfo(layer: ILayerProps) {
            const { label } = layer.data as IMarkupLayerProps;
            let text = '';
            if (label.type === MarkupLabelType.num) {
                //  text =[...new Set('.0123456789designkitmmcminch')].join('');
                text = '.0123456789designktmch/';
            } else {
                text = LabelKit.getRenderText(label);
            }
            return [
                {
                    text,
                    fontFamily: label.fontFamily,
                },
            ];
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.MARKUP_LAYER,
    getBehavior,
};

export default exportBehavior;
